import React, { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { boardService } from '../../../lib/api/boards';
import { useAuthStore } from '../../../stores/authStore';
import { currencyService, Currency } from '../../../lib/api/currency';

interface BoardOperationCreationFormProps {
    isOpen: boolean;
    onClose: () => void;
    boardId: string;
    journalId: string;
    standalone?: boolean;
    existingTitles?: string[];
    operation?: any;
    associatedBoardId?: string;
    onOpenDocuments?: () => void;
    onOperationCreated?: (opId: string) => void;
}

export const BoardOperationCreationForm: React.FC<BoardOperationCreationFormProps> = ({
    isOpen,
    onClose,
    boardId,
    journalId,
    standalone = false,
    existingTitles = [],
    operation,
    associatedBoardId,
    onOpenDocuments,
    onOperationCreated
}) => {
    const { t } = useTranslation('common');
    const queryClient = useQueryClient();
    const titleInputRef = useRef<HTMLInputElement>(null);
    const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>([]);

    useEffect(() => {
        const fetchCurrencies = async () => {
            try {
                const response = await currencyService.getCurrencies();
                if (response.success) {
                    setAvailableCurrencies(response.data.currencies);
                }
            } catch (error) {
                console.error("Failed to fetch currencies", error);
            }
        };
        fetchCurrencies();
    }, []);

    const generateDefaultTitle = () => {
        if (!existingTitles || existingTitles.length === 0) return t('boardJournals.details.defaultOperationName', { number: 1 });

        const operationParams = existingTitles
            .map((title: string) => {
                const match = title.match(/^Operation\s+(\d+)$/i);
                return match ? parseInt(match[1], 10) : 0;
            })
            .filter((num: number) => num > 0);

        const maxNum = operationParams.length > 0 ? Math.max(...operationParams) : 0;
        return t('boardJournals.details.defaultOperationName', { number: maxNum + 1 });
    };

    const { user } = useAuthStore();
    const defaultCurrency = user?.currency || '';

    // Default new operations to yesterday — users typically enter costs the day
    // after they occur, so pre-selecting today leaves them one click short.
    const getDefaultOperationDate = () => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d.toISOString().split('T')[0];
    };

    const [newItem, setNewItem] = useState({
        title: '',
        amount: '',
        type: 'EXPENSE' as 'REVENUE' | 'EXPENSE',
        date: getDefaultOperationDate(),
        isPeriodic: false,
        frequency: 'monthly',
        endDate: '',
        currency: defaultCurrency
    });

    useEffect(() => {
        if (isOpen) {
            if (operation) {
                // Edit mode
                setNewItem({
                    title: operation.description || operation.category || '',
                    amount: operation.amount?.toString() || '',
                    type: (operation.type === 'income' || operation.type === 'REVENUE') ? 'REVENUE' : 'EXPENSE',
                    date: operation.date ? new Date(operation.date).toISOString().split('T')[0] : getDefaultOperationDate(),
                    isPeriodic: !!operation.recurrence || (!!operation.startDate && !!operation.frequency),
                    frequency: operation.recurrence?.frequency || operation.frequency || 'monthly',
                    endDate: operation.recurrence?.endDate ? new Date(operation.recurrence.endDate).toISOString().split('T')[0] : '',
                    currency: operation.currency
                });
            } else {
                // Create mode
                setNewItem(prev => ({
                    ...prev,
                    title: generateDefaultTitle(),
                    amount: '',
                    type: 'EXPENSE',
                    date: getDefaultOperationDate(),
                    isPeriodic: false,
                    frequency: 'monthly',
                    endDate: '',
                    currency: defaultCurrency
                }));
            }
        }
    }, [isOpen, operation, defaultCurrency]);

    const [errors, setErrors] = useState<Record<string, boolean>>({});

    const updateNewItem = (updates: Partial<typeof newItem>) => {
        setNewItem(prev => {
            const updated = { ...prev, ...updates };
            // Clear errors for fields that now have values
            if (errors.title && updated.title) setErrors(e => ({ ...e, title: false }));
            if (errors.amount && updated.amount) setErrors(e => ({ ...e, amount: false }));
            if (errors.currency && updated.currency) setErrors(e => ({ ...e, currency: false }));
            return updated;
        });
    };

    const handleSave = async () => {
        const newErrors: Record<string, boolean> = {};
        if (!newItem.title) newErrors.title = true;
        if (!newItem.amount) newErrors.amount = true;
        if (!newItem.currency) newErrors.currency = true;

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});

        try {
            const payload: any = {
                description: newItem.title,
                amount: parseFloat(newItem.amount),
                type: newItem.type === 'REVENUE' ? 'income' : 'expense',
                currency: newItem.currency,
            };

            if (newItem.isPeriodic) {
                payload.recurrence = {
                    startDate: newItem.date,
                    frequency: newItem.frequency,
                    endDate: newItem.endDate || undefined
                };
            } else {
                payload.date = newItem.date;
            }

            if (operation) {
                // Update
                const updatePayload = {
                    ...payload,
                    metadata: operation.metadata || operation.raw?.metadata // Preserve existing metadata (files, etc.)
                };

                if (standalone) {
                    await boardService.updateGlobalJournalOperation(journalId, operation.id, updatePayload);
                } else {
                    await boardService.updateJournalOperation(boardId, journalId, operation.id, updatePayload);
                }
            } else {
                // Create
                let createdOp: any;
                if (standalone) {
                    createdOp = await boardService.createGlobalJournalOperation(journalId, payload);
                } else {
                    createdOp = await boardService.createJournalTransaction(boardId, journalId, payload);
                }
                if (createdOp?.id && onOperationCreated) {
                    onOperationCreated(createdOp.id);
                }
            }

            // Invalidate journal operations with a pattern that hits both global and board-specific caches if they exist
            // By invalidating the prefix, we ensure UI consistency across different views
            queryClient.invalidateQueries({ queryKey: ['journal-operations'] });

            // If we have a board ID (either direct or associated), invalidate its data
            const bId = boardId || associatedBoardId;
            if (bId) {
                queryClient.invalidateQueries({ queryKey: ['board-payload', bId] });
            }

            onClose();
            setErrors({});
            if (!operation) {
                // Only reset if creating, though onClose usually unmounts or hides so might not matter
                setNewItem({
                    title: '',
                    amount: '',
                    type: 'EXPENSE',
                    date: getDefaultOperationDate(),
                    isPeriodic: false,
                    frequency: 'monthly',
                    endDate: '',
                    currency: defaultCurrency
                });
            }
        } catch (error: any) {
            console.error('Failed to save operation:', error);
        }
    };

    // Focus management
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                titleInputRef.current?.focus({ preventScroll: true });
            }, 300);
            return () => clearTimeout(timer);
        } else {
            setErrors({});
        }
    }, [isOpen]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    const isEditMode = !!operation;

    const formContainerRef = useRef<HTMLDivElement>(null);

    // Click outside to save
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (formContainerRef.current && !formContainerRef.current.contains(event.target as Node)) {
                handleSave();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, newItem, errors]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    ref={formContainerRef}
                    initial={isEditMode ? { opacity: 0, y: 100 } : { height: 0, opacity: 0 }}
                    animate={isEditMode ? { opacity: 1, y: 0 } : { height: "auto", opacity: 1 }}
                    exit={isEditMode ? { opacity: 0, y: 100 } : { height: 0, opacity: 0 }}
                    transition={{ duration: isEditMode ? 0.3 : 0.4, ease: [0.32, 0.72, 0, 1] }}
                    className="fixed bottom-0 left-0 right-0 z-50 lg:relative lg:inset-auto lg:z-0 bg-white/98 dark:bg-neutral-900/98 backdrop-blur-xl lg:backdrop-blur-none lg:bg-transparent border-t border-neutral-200/60 dark:border-neutral-800 lg:border-none shadow-[0_-12px_40px_rgba(0,0,0,0.15)] lg:shadow-none px-4 lg:px-3 pt-5 pb-safe lg:py-0 overflow-hidden rounded-t-[32px] lg:rounded-none max-h-[90dvh] overflow-y-auto"
                >
                    {/* Desktop View: Grid Layout Matching List (2-5-3-2 matches list header) */}
                    <div className="hidden lg:grid grid-cols-12 gap-4 items-center w-full py-2">

                        {/* Column 1 (span 3): Type + Amount + Currency */}
                        <div className="col-span-3 flex items-center gap-1.5 min-w-0">
                            {/* Type Selection — icon-only to fit the narrower column */}
                            <button
                                onClick={() => updateNewItem({ type: newItem.type === 'REVENUE' ? 'EXPENSE' : 'REVENUE' })}
                                title={newItem.type === 'REVENUE' ? (t('common.revenue') as string) : (t('common.expense') as string)}
                                className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-lg transition-all duration-200 ${newItem.type === 'REVENUE'
                                    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-900/30 hover:bg-emerald-100/60 dark:hover:bg-emerald-800/50'
                                    : 'text-red-600 dark:text-red-400 bg-red-50/40 dark:bg-red-900/30 hover:bg-red-100/60 dark:hover:bg-red-800/50'
                                    }`}
                            >
                                {newItem.type === 'REVENUE' ? (
                                    <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_17e64163.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_17e64163.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                ) : (
                                    <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_d1b707dd.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d1b707dd.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                )}
                            </button>

                            {/* Amount & Currency */}
                            <div className={`flex-1 flex items-center gap-1 border-b ${errors.amount ? 'border-red-500' : 'border-transparent'} hover:border-neutral-200 dark:hover:border-neutral-700 focus-within:border-accent-primary transition-colors min-w-0`}>
                                <div className="relative group/currency">
                                    <select
                                        name="op-curr-d"
                                        id="op-curr-d"
                                        autoComplete="off"
                                        data-1p-ignore
                                        data-lpignore="true"
                                        data-bwignore="true"
                                        data-form-type="other"
                                        value={newItem.currency}
                                        onChange={(e) => updateNewItem({ currency: e.target.value })}
                                        className={`appearance-none bg-neutral-100/80 dark:bg-neutral-800/80 hover:bg-neutral-200/80 dark:hover:bg-neutral-700/80 text-neutral-700 dark:text-neutral-300 text-[11px] font-bold pl-2 pr-5 py-1 rounded-md cursor-pointer transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-accent-primary/50 text-center ${errors.currency ? 'ring-2 ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}`}
                                        title={t("currency.selectCurrency") || "Select Currency"}
                                    >
                                        <option value="" disabled>--</option>
                                        {availableCurrencies.map(curr => (
                                            <option key={curr.code} value={curr.code}>
                                                {curr.symbol}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-500">
                                        <span className="w-3 h-3" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_446d4857.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_446d4857.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    </div>
                                </div>
                                <input
                                    type="number"
                                    name="op-amt-d"
                                    id="op-amt-d"
                                    autoComplete="off"
                                    data-1p-ignore
                                    data-lpignore="true"
                                    data-bwignore="true"
                                    data-form-type="other"
                                    ref={titleInputRef}
                                    className="block w-full px-1 py-1 text-sm font-mono font-medium text-neutral-900 dark:text-neutral-200 bg-transparent border-none focus:outline-none transition-colors placeholder-neutral-400 dark:placeholder-neutral-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none min-w-0"
                                    placeholder={t('boardJournals.details.amountPlaceholder')}
                                    value={newItem.amount}
                                    onChange={(e) => updateNewItem({ amount: e.target.value })}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                        </div>

                        {/* Column 2 (span 4): Title */}
                        <div className="col-span-4 min-w-0">
                            <input
                                type="text"
                                name="op-title-d"
                                id="op-title-d"
                                autoComplete="off"
                                data-1p-ignore
                                data-lpignore="true"
                                data-bwignore="true"
                                data-form-type="other"
                                className={`block w-full px-0 py-1 text-sm font-medium text-neutral-900 dark:text-neutral-200 bg-transparent border-b ${errors.title ? 'border-red-500' : 'border-transparent'} hover:border-neutral-200 dark:hover:border-neutral-700 focus:border-accent-primary focus:outline-none transition-colors placeholder-neutral-400 dark:placeholder-neutral-500`}
                                placeholder={t('boardJournals.details.headers.operation') || 'Operation'}
                                value={newItem.title}
                                onChange={(e) => updateNewItem({ title: e.target.value })}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        {/* Column 3 (span 3): Date & Periodic */}
                        <div className="col-span-3 flex items-center gap-2">
                            {/* Date Input */}
                            <div className="relative group flex-shrink-0">
                                <input
                                    type="date"
                                    name="op-date-d"
                                    id="op-date-d"
                                    autoComplete="off"
                                    data-1p-ignore
                                    data-lpignore="true"
                                    data-bwignore="true"
                                    data-form-type="other"
                                    className="block w-[110px] px-0 py-1 text-sm font-medium text-neutral-500 dark:text-neutral-400 bg-transparent border-b border-transparent hover:border-neutral-200 dark:hover:border-neutral-700 focus:border-accent-primary focus:outline-none transition-colors focus:text-neutral-900 dark:focus:text-neutral-200"
                                    value={newItem.date}
                                    onChange={(e) => updateNewItem({ date: e.target.value })}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>

                            {/* Periodic Toggle */}
                            <button
                                onClick={() => updateNewItem({ isPeriodic: !newItem.isPeriodic })}
                                className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg transition-all duration-200 ${newItem.isPeriodic ? 'text-accent-primary bg-accent-primary/10 dark:bg-accent-primary/20 border border-accent-primary/20 dark:border-accent-primary/30' : 'text-neutral-300 dark:text-neutral-600 hover:text-neutral-500 dark:hover:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-transparent'}`}
                                title={t('boardJournals.details.recurring') || 'Recurring'}
                            >
                                <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_e13d509c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_e13d509c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            </button>

                            {/* Recurrence Options */}
                            <AnimatePresence>
                                {newItem.isPeriodic && (
                                    <motion.div
                                        initial={{ opacity: 0, width: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, width: 'auto', scale: 1 }}
                                        exit={{ opacity: 0, width: 0, scale: 0.95 }}
                                        className="overflow-hidden"
                                    >
                                        <select
                                            name="op-freq-d"
                                            id="op-freq-d"
                                            autoComplete="off"
                                            data-1p-ignore
                                            data-lpignore="true"
                                            data-bwignore="true"
                                            data-form-type="other"
                                            className="bg-neutral-50/60 dark:bg-neutral-800/60 hover:bg-neutral-100/80 dark:hover:bg-neutral-700/80 text-neutral-600 dark:text-neutral-400 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-lg border border-transparent focus:ring-0 focus:outline-none appearance-none cursor-pointer"
                                            value={newItem.frequency}
                                            onChange={(e) => updateNewItem({ frequency: e.target.value })}
                                        >
                                            <option value="daily">{t('boardJournals.details.frequencies.daily')}</option>
                                            <option value="weekly">{t('boardJournals.details.frequencies.weekly')}</option>
                                            <option value="monthly">{t('boardJournals.details.frequencies.monthly')}</option>
                                            <option value="yearly">{t('boardJournals.details.frequencies.yearly')}</option>
                                        </select>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Column 4 (span 2): Actions */}
                        <div className="col-span-2 flex items-center justify-end gap-1">
                            <button
                                onClick={onClose}
                                className="p-1.5 rounded-md text-neutral-400 dark:text-neutral-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                                title={t('actions.cancel') || 'Cancel'}
                            >
                                <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_0a0a1871.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_0a0a1871.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            </button>
                            <button
                                onClick={handleSave}
                                className="p-1.5 rounded-md text-neutral-400 dark:text-neutral-500 hover:text-emerald-500 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950 transition-colors"
                                title={t('actions.confirm') || 'Confirm'}
                            >
                                <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_77d7ad51.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_77d7ad51.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            </button>
                        </div>
                    </div>

                    {/* Mobile View: Vertical "Bottom Sheet" Style */}
                    <div className="flex lg:hidden flex-col gap-6">
                        {/* Row 1: Type + Amount + Currency */}
                        <div className="flex items-center gap-3">
                            {/* Type */}
                            <button
                                onClick={() => updateNewItem({ type: newItem.type === 'REVENUE' ? 'EXPENSE' : 'REVENUE' })}
                                className={`w-[52px] h-[38px] flex-shrink-0 flex items-center justify-center rounded-xl transition-all duration-200 ${newItem.type === 'REVENUE'
                                    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-900/30'
                                    : 'text-red-600 dark:text-red-400 bg-red-50/40 dark:bg-red-900/30'
                                    }`}
                            >
                                {newItem.type === 'REVENUE' ? (
                                    <span className="w-4.5 h-4.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_17e64163.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_17e64163.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                ) : (
                                    <span className="w-4.5 h-4.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_d1b707dd.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d1b707dd.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                )}
                            </button>

                            {/* Amount & Currency */}
                            <div className={`flex-1 flex items-center gap-2 bg-neutral-50/60 dark:bg-neutral-800/60 rounded-xl px-3 py-2 border ${errors.amount ? 'border-red-500' : 'border-transparent'} focus-within:border-neutral-200 dark:focus-within:border-neutral-700 transition-colors`}>
                                <select
                                    name="op-curr-m"
                                    id="op-curr-m"
                                    autoComplete="off"
                                    data-1p-ignore
                                    data-lpignore="true"
                                    data-bwignore="true"
                                    data-form-type="other"
                                    value={newItem.currency}
                                    onChange={(e) => updateNewItem({ currency: e.target.value })}
                                    className="appearance-none text-neutral-600 dark:text-neutral-400 text-[10px] font-black bg-transparent border-none focus:ring-0 focus:outline-none"
                                >
                                    {availableCurrencies.map(curr => (
                                        <option key={curr.code} value={curr.code}>{curr.symbol}</option>
                                    ))}
                                </select>
                                <input
                                    type="number"
                                    name="op-amt-m"
                                    id="op-amt-m"
                                    autoComplete="off"
                                    data-1p-ignore
                                    data-lpignore="true"
                                    data-bwignore="true"
                                    data-form-type="other"
                                    className="flex-1 min-w-0 bg-transparent border-none p-0 text-lg font-mono font-medium text-neutral-900 dark:text-neutral-200 focus:ring-0 focus:outline-none placeholder-neutral-300 dark:placeholder-neutral-600"
                                    placeholder={t('boardJournals.details.amountPlaceholder')}
                                    value={newItem.amount}
                                    onChange={(e) => updateNewItem({ amount: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Row 2: Title */}
                        <div className="relative group">
                            <label className="text-[10px] font-black text-neutral-400 dark:text-neutral-500 uppercase tracking-wider ml-1 mb-1 block">{t('boardJournals.details.headers.operation')}</label>
                            <input
                                type="text"
                                name="op-title-m"
                                id="op-title-m"
                                autoComplete="off"
                                data-1p-ignore
                                data-lpignore="true"
                                data-bwignore="true"
                                data-form-type="other"
                                className="block w-full py-2 text-base font-medium text-neutral-900 dark:text-neutral-200 bg-transparent border-b border-neutral-100 dark:border-neutral-800 focus:border-accent-primary focus:ring-0 focus:outline-none placeholder-neutral-300 dark:placeholder-neutral-600 transition-colors"
                                placeholder={t('boardJournals.details.headers.operation') || 'Operation Name'}
                                value={newItem.title}
                                onChange={(e) => updateNewItem({ title: e.target.value })}
                            />
                        </div>

                        {/* Row 3: Date */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[10px] font-black text-neutral-400 dark:text-neutral-500 uppercase tracking-wider ml-1">{t('boardJournals.details.headers.date')}</label>
                            <div className="bg-neutral-50/60 dark:bg-neutral-800/60 rounded-xl px-3 py-2.5 border border-transparent focus-within:border-neutral-200 dark:focus-within:border-neutral-700 transition-colors">
                                <input
                                    type="date"
                                    name="op-date-m"
                                    id="op-date-m"
                                    autoComplete="off"
                                    data-1p-ignore
                                    data-lpignore="true"
                                    data-bwignore="true"
                                    data-form-type="other"
                                    className="w-full bg-transparent border-none p-0 text-sm font-medium text-neutral-700 dark:text-neutral-300 focus:ring-0 focus:outline-none"
                                    value={newItem.date}
                                    onChange={(e) => updateNewItem({ date: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Mobile Actions Bar: All-in-one line */}
                        <div className="flex flex-wrap items-center gap-2 pt-2">
                            <button
                                onClick={() => updateNewItem({ isPeriodic: !newItem.isPeriodic })}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition-all duration-200 ${newItem.isPeriodic ? 'text-accent-primary bg-accent-primary/10 dark:bg-accent-primary/20 border border-accent-primary/20 dark:border-accent-primary/30' : 'text-neutral-500 dark:text-neutral-400 bg-neutral-50/60 dark:bg-neutral-800/60 border border-transparent'}`}
                            >
                                <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_98627734.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_98627734.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                <span className="text-[10px] font-black uppercase tracking-wider">{t('boardJournals.details.recurring')}</span>
                            </button>

                            {isEditMode && onOpenDocuments && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onOpenDocuments(); }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-accent-primary/10 text-accent-primary border border-accent-primary/20 hover:bg-accent-primary/20 transition-all"
                                >
                                    <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_79780f52.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_79780f52.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    <span className="text-[10px] font-black uppercase tracking-wider">{t('common.viewFiles')}</span>
                                </button>
                            )}

                            <AnimatePresence>
                                {newItem.isPeriodic && (
                                    <motion.div
                                        initial={{ opacity: 0, width: 0, x: -10 }}
                                        animate={{ opacity: 1, width: 'auto', x: 0 }}
                                        exit={{ opacity: 0, width: 0, x: -10 }}
                                        className="overflow-hidden"
                                    >
                                        <select
                                            autoComplete="off"
                                            data-1p-ignore
                                            data-lpignore="true"
                                            data-bwignore="true"
                                            data-form-type="other"
                                            className="bg-neutral-50/60 dark:bg-neutral-800/60 text-neutral-600 dark:text-neutral-400 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-xl border border-transparent focus:ring-0 focus:outline-none appearance-none cursor-pointer"
                                            value={newItem.frequency}
                                            onChange={(e) => updateNewItem({ frequency: e.target.value })}
                                        >
                                            <option value="daily">{t('boardJournals.details.frequencies.daily')}</option>
                                            <option value="weekly">{t('boardJournals.details.frequencies.weekly')}</option>
                                            <option value="monthly">{t('boardJournals.details.frequencies.monthly')}</option>
                                            <option value="yearly">{t('boardJournals.details.frequencies.yearly')}</option>
                                        </select>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="flex items-center gap-2 ml-auto">
                                <button
                                    onClick={onClose}
                                    className="p-2.5 text-neutral-400 dark:text-neutral-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 rounded-xl transition-colors"
                                    title={t('actions.cancel') || 'Cancel'}
                                >
                                    <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_2e46f7c6.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_2e46f7c6.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="p-2.5 text-neutral-400 dark:text-neutral-500 hover:text-emerald-500 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950 rounded-xl transition-colors"
                                    title={t('actions.confirm') || 'Confirm'}
                                >
                                    <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_fe3c456e.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_fe3c456e.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
